/**
 * @fileoverview Type definitions for the milestone management system
 * 
 * This file contains all TypeScript interfaces and types for managing
 * project milestones, including:
 * - Milestone entities: Key deliverable points in a job's timeline
 * - Milestone approvals: Mutual approval system between client and vendor
 * - Milestone comments: Discussion and clarification on specific milestones
 * - Change history: Audit trail of milestone modifications
 * 
 * Milestones represent the agreed-upon project timeline with specific
 * deliverables, owners (client or vendor), and due dates. Both parties
 * must approve the timeline before work can begin.
 * 
 * @module lib/types/milestone
 */

/**
 * Type of milestone activity
 * 
 * Categorizes milestones by the type of work or deliverable involved.
 */
export type MilestoneType =
  | 'planning'
  | 'design'
  | 'development'
  | 'review'
  | 'testing'
  | 'delivery'
  | 'client_approval'
  | 'installation';

/**
 * System milestone types
 * 
 * Represents auto-tracked workflow stages that are managed by the system.
 */
export type SystemMilestoneType =
  | 'job_created'
  | 'specifications_complete'
  | 'delivery_details_complete'
  | 'rfq_published'
  | 'vendor_awarded'
  | 'milestones_approved'
  | 'purchase_order_created'
  | 'in_progress'
  | 'delivery_proof_uploaded'
  | 'job_completion_submitted'
  | 'job_accepted'
  | 'job_closed';

/**
 * Indicates which party is responsible for completing this milestone
 */
export type MilestoneActionOwner = 'client' | 'vendor';

/**
 * Approval status of the milestone timeline
 * 
 * Tracks whether client, vendor, or both have approved/rejected the schedule.
 * Both parties must approve before work can proceed.
 */
export type MilestoneApprovalStatus =
  | 'pending_client'
  | 'pending_vendor'
  | 'pending_both'
  | 'approved_by_client'
  | 'approved_by_vendor'
  | 'approved_by_both'
  | 'rejected_by_client'
  | 'rejected_by_vendor'
  | 'change_requested_by_client'
  | 'change_requested_by_vendor';

/**
 * Main milestone entity
 * 
 * Represents a single deliverable or checkpoint in the project timeline.
 * Each milestone has a due date, owner, and completion status.
 */
export interface Milestone {
  id: string;
  public_id: string;
  job_id: string;
  purchase_order_id?: string;
  organization_id: string;
  milestone_name: string;
  due_date: string;
  description?: string;
  milestone_type: MilestoneType;
  deliverable: string;
  action_owner: MilestoneActionOwner;
  milestone_order: number;
  is_blank: boolean;
  is_locked: boolean;
  is_completed: boolean;
  completed_at?: string;
  completed_by_user_id?: string;
  show_upload_button?: boolean;
  created_by_user_id: string;
  last_updated_by_user_id: string;
  created_at: string;
  updated_at: string;
  is_system_milestone: boolean;
  system_milestone_type?: SystemMilestoneType;
}

/**
 * Milestone with associated job information
 * 
 * Extended milestone interface that includes job context for display purposes.
 * Used in milestone list views and calendar displays.
 */
export interface MilestoneWithJob extends Milestone {
  job_title: string;
  job_public_id: string;
  job_status: string;
}

/**
 * Milestone approval record
 * 
 * Tracks whether both parties (client and vendor) have approved the
 * milestone timeline for a job. Both approvals required before work begins.
 */
export interface MilestoneApproval {
  id: string;
  job_id: string;
  organization_id: string;
  client_approved_at?: string;
  client_approved_by_user_id?: string;
  vendor_approved_at?: string;
  vendor_approved_by_user_id?: string;
  approval_status: MilestoneApprovalStatus;
  milestone_version: number;
  created_at: string;
  updated_at: string;
}

/**
 * Comment on a specific milestone
 * 
 * Allows discussion, questions, and clarifications about individual milestones.
 */
export interface MilestoneComment {
  id: string;
  milestone_id: string;
  organization_id: string;
  commenter_user_id: string;
  comment_text: string;
  created_at: string;
  updated_at: string;
}

/**
 * Historical record of milestone changes
 * 
 * Audit trail tracking all modifications to milestones for accountability.
 */
export interface MilestoneChangeHistory {
  id: string;
  milestone_id: string;
  organization_id: string;
  changed_by_user_id: string;
  change_type: 'created' | 'updated' | 'deleted';
  field_name: string;
  old_value?: string;
  new_value?: string;
  change_reason?: string;
  created_at: string;
}

/**
 * Data required to create a new milestone
 */
export interface CreateMilestoneData {
  milestone_name: string;
  due_date: string;
  description?: string;
  milestone_type: MilestoneType;
  deliverable: string;
  action_owner: MilestoneActionOwner;
  milestone_order: number;
  is_blank?: boolean;
  show_upload_button?: boolean;
}

/**
 * Data for updating an existing milestone (all fields optional)
 */
export interface UpdateMilestoneData {
  milestone_name?: string;
  due_date?: string;
  description?: string;
  milestone_type?: MilestoneType;
  deliverable?: string;
  action_owner?: MilestoneActionOwner;
  milestone_order?: number;
  is_blank?: boolean;
  show_upload_button?: boolean;
}

/**
 * Data for requesting a change to a milestone with justification
 */
export interface RequestMilestoneChangeData {
  milestone_name?: string;
  due_date?: string;
  description?: string;
  milestone_type?: MilestoneType;
  deliverable?: string;
  action_owner?: MilestoneActionOwner;
  milestone_order?: number;
  change_reason?: string;
}

/** Milestone type options for UI selection */
export const MILESTONE_TYPES: { value: MilestoneType; label: string }[] = [
  { value: 'planning', label: 'Planning' },
  { value: 'design', label: 'Design' },
  { value: 'development', label: 'Development' },
  { value: 'review', label: 'Review' },
  { value: 'testing', label: 'Testing' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'client_approval', label: 'Client Approval' },
  { value: 'installation', label: 'Installation' },
];

/** Action owner options for UI selection */
export const ACTION_OWNERS: { value: MilestoneActionOwner; label: string }[] = [
  { value: 'client', label: 'Client' },
  { value: 'vendor', label: 'Vendor' },
];

/**
 * Pending milestone change
 * 
 * Represents a proposed change to a milestone that requires approval
 * from the other party before being applied.
 */
export interface PendingMilestoneChange {
  id: string;
  milestone_id: string;
  job_id: string;
  organization_id: string;
  requested_by_user_id: string;
  requested_by_type: 'client' | 'vendor';
  change_reason?: string;
  pending_changes: Record<string, any>; // JSONB of field changes
  status: 'pending' | 'approved' | 'declined';
  responded_by_user_id?: string;
  responded_at?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Milestone phase groupings for unified timeline display
 */
export type MilestonePhase = 'setup' | 'award' | 'project' | 'completion';

/**
 * System milestone configuration with phase and order
 */
export const SYSTEM_MILESTONE_CONFIG: Record<SystemMilestoneType, {
  phase: MilestonePhase;
  order: number;
  label: string;
}> = {
  job_created: { phase: 'setup', order: 1, label: 'Job Created' },
  specifications_complete: { phase: 'setup', order: 2, label: 'Specifications Complete' },
  delivery_details_complete: { phase: 'setup', order: 3, label: 'Delivery Details Complete' },
  rfq_published: { phase: 'setup', order: 4, label: 'RFQ Published' },
  vendor_awarded: { phase: 'award', order: 5, label: 'Vendor Awarded' },
  milestones_approved: { phase: 'award', order: 6, label: 'Milestones Approved' },
  purchase_order_created: { phase: 'award', order: 7, label: 'Purchase Order Created' },
  in_progress: { phase: 'award', order: 8, label: 'Work In Progress' },
  delivery_proof_uploaded: { phase: 'completion', order: 9, label: 'Delivery Proof Uploaded' },
  job_completion_submitted: { phase: 'completion', order: 10, label: 'Job Completion Submitted' },
  job_accepted: { phase: 'completion', order: 11, label: 'Job Accepted' },
  job_closed: { phase: 'completion', order: 12, label: 'Job Closed' },
};
